import React, { useCallback, useEffect, useState } from "react";
import { Music, Send, Loader2, Sparkles, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import MotionReveal from "@/components/MotionReveal";
import NowPlaying from "@/components/NowPlaying";
import api from "@/lib/api";
import { rememberMyTrack } from "@/lib/djCelebration";
import { useLang } from "@/contexts/LangContext";

/** Public DJ page — guests submit songs during the party; auto-synced to Spotify. */
const Dj = () => {
  const { t } = useLang();
  const [song, setSong] = useState("");
  const [fromName, setFromName] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [recent, setRecent] = useState([]);

  const loadRecent = useCallback(async () => {
    try {
      const { data } = await api.get("/dj/recent");
      setRecent(data.recent || []);
    } catch {
      // silent — page still works without the recently-added wall
    }
  }, []);

  useEffect(() => {
    loadRecent();
  }, [loadRecent]);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = song.trim();
    if (trimmed.length < 2) {
      toast.error(t("dj.errTooShort") || "Type a song title (at least 2 characters).");
      return;
    }
    setBusy(true);
    setLastResult(null);
    try {
      const { data } = await api.post("/dj/request", {
        song: trimmed,
        from_name: fromName.trim(),
      });
      setLastResult(data);
      if (data.status === "added") {
        rememberMyTrack(data.track);
        toast.success(t("dj.toastAdded") || "On the playlist. Thank you!");
        setSong("");
        await loadRecent();
      } else if (data.status === "already_added") {
        rememberMyTrack(data.track);
        toast.message(t("dj.toastDuplicate") || "Already there — good taste.");
        setSong("");
        await loadRecent();
      } else if (data.status === "not_found") {
        toast.error(data.message || t("dj.toastNotFound") || "We couldn't find that one — try adding the artist.");
      } else if (data.status === "queued") {
        toast.message(data.message || t("dj.toastQueued") || "Got your request!");
        setSong("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("dj.errGeneric") || "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="dj-page">
      {/* ─── Hero ────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-[860px] px-5 sm:px-8 pt-24 sm:pt-32">
        <MotionReveal>
          <p className="editorial-kicker inline-flex items-center gap-2">
            <Music className="w-3.5 h-3.5" />
            {t("dj.kicker") || "The DJ booth"}
          </p>
        </MotionReveal>
        <MotionReveal delay={0.05}>
          <h1 className="editorial-h1 mt-6 text-4xl sm:text-6xl lg:text-7xl">
            {t("dj.title") || "Request a song."}
          </h1>
        </MotionReveal>
        <MotionReveal delay={0.1}>
          <p className="mt-8 text-foreground/75 max-w-[54ch] leading-relaxed text-lg font-serif-display italic">
            {t("dj.intro") ||
              "Type a song, hit send. It lands on the wedding playlist within seconds. Dance with us."}
          </p>
        </MotionReveal>
      </section>

      {/* ─── Form ────────────────────────────────────────────────────── */}
      <section className="mx-auto max-w-[860px] px-5 sm:px-8 mt-14 sm:mt-20">
        {/* Live now-playing widget — shows only while a track is actually playing */}
        <MotionReveal delay={0.12}>
          <div className="mb-10">
            <NowPlaying />
          </div>
        </MotionReveal>

        <MotionReveal delay={0.15}>
          <form onSubmit={submit} className="space-y-8" data-testid="dj-form">
            <div className="space-y-2">
              <Label htmlFor="dj-song" className="editorial-kicker">
                {t("dj.songLabel") || "Song title (add the artist if you can)"}
              </Label>
              <Input
                id="dj-song"
                value={song}
                onChange={(e) => setSong(e.target.value)}
                placeholder={t("dj.songPlaceholder") || "Dancing Queen — ABBA"}
                maxLength={200}
                autoFocus
                data-testid="dj-song-input"
                className="h-14 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent font-serif-display text-2xl sm:text-3xl px-0"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="dj-name" className="editorial-kicker">
                {t("dj.nameLabel") || "Your name (optional — appears below)"}
              </Label>
              <Input
                id="dj-name"
                value={fromName}
                onChange={(e) => setFromName(e.target.value)}
                placeholder={t("dj.namePlaceholder") || "Marie"}
                maxLength={80}
                data-testid="dj-name-input"
                className="h-12 border-0 border-b border-border rounded-none bg-transparent focus-visible:ring-0 focus-visible:border-accent px-0"
              />
            </div>

            <div className="pt-2">
              <Button
                type="submit"
                disabled={busy}
                data-testid="dj-submit"
                className="h-12 px-8 rounded-xl bg-foreground text-background hover:bg-foreground/90 tracking-[0.22em] uppercase text-[12px] gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {busy
                  ? t("dj.sending") || "Sending…"
                  : t("dj.sendCta") || "Send to the DJ"}
              </Button>
              <p className="mt-3 editorial-kicker text-foreground/45">
                {t("dj.helper") || "Auto-added to the Spotify playlist · duplicates are skipped"}
              </p>
            </div>
          </form>
        </MotionReveal>

        {/* ─── Last-submitted feedback card ─── */}
        {lastResult && (lastResult.status === "added" || lastResult.status === "already_added") && lastResult.track && (
          <MotionReveal>
            <div
              className="mt-10 surface-card p-5 flex items-center gap-4"
              data-testid="dj-last-result"
            >
              {lastResult.track.cover ? (
                <img src={lastResult.track.cover} alt="" className="w-16 h-16 rounded object-cover shrink-0" />
              ) : (
                <div className="w-16 h-16 rounded bg-foreground/5 flex items-center justify-center shrink-0">
                  <Music className="w-6 h-6 text-foreground/30" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="editorial-kicker text-accent inline-flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  {lastResult.status === "added"
                    ? t("dj.addedKicker") || "Added"
                    : t("dj.dupKicker") || "Already there"}
                </p>
                <p className="mt-1 font-serif-display text-lg truncate">{lastResult.track.name}</p>
                <p className="text-sm text-foreground/60 truncate">{lastResult.track.artist}</p>
              </div>
              {lastResult.track.url && (
                <a
                  href={lastResult.track.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 h-9 px-3 inline-flex items-center gap-1.5 rounded-md border border-border hover:border-foreground/40 text-foreground/60 hover:text-foreground text-[11px] tracking-[0.18em] uppercase transition-colors"
                  data-testid="dj-last-result-link"
                >
                  {t("dj.openInSpotify") || "Open"} <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </MotionReveal>
        )}
      </section>

      {/* ─── Recently added wall ─── */}
      <section className="mx-auto max-w-[1120px] px-5 sm:px-8 mt-20 sm:mt-28 pb-24" data-testid="dj-recent-section">
        <MotionReveal>
          <div className="flex items-baseline justify-between gap-6 flex-wrap">
            <div>
              <p className="editorial-kicker">{t("dj.recentKicker") || "On the playlist"}</p>
              <h2 className="editorial-h2 mt-3 text-2xl sm:text-3xl">
                {t("dj.recentTitle") || "Recently added by our guests."}
              </h2>
            </div>
            {recent.length > 0 && (
              <p className="text-xs editorial-kicker text-foreground/50">
                {recent.length} {t("dj.recentSuffix") || "so far"}
              </p>
            )}
          </div>
        </MotionReveal>

        {recent.length === 0 ? (
          <MotionReveal delay={0.05}>
            <div className="mt-10 py-16 text-center border border-dashed border-border rounded-xl" data-testid="dj-recent-empty">
              <Music className="w-8 h-8 mx-auto text-foreground/25" />
              <p className="mt-4 font-serif-display italic text-lg text-foreground/60">
                {t("dj.recentEmpty") || "No songs yet. Be the first."}
              </p>
            </div>
          </MotionReveal>
        ) : (
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dj-recent-grid">
            {recent.map((r, i) => (
              <MotionReveal key={i} delay={Math.min(0.05 + i * 0.02, 0.3)}>
                <a
                  href={r.spotify_url || undefined}
                  target={r.spotify_url ? "_blank" : undefined}
                  rel="noopener noreferrer"
                  className="surface-card p-3 flex items-center gap-3 hover:border-foreground/40 transition-colors group"
                  data-testid={`dj-recent-item-${i}`}
                >
                  {r.cover ? (
                    <img src={r.cover} alt="" className="w-14 h-14 rounded object-cover shrink-0" />
                  ) : (
                    <div className="w-14 h-14 rounded bg-foreground/5 flex items-center justify-center shrink-0">
                      <Music className="w-5 h-5 text-foreground/30" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate text-sm">{r.track_name || r.song}</p>
                    <p className="text-xs text-foreground/55 truncate">{r.artist || "—"}</p>
                    {r.from_name && (
                      <p className="mt-1 text-[11px] editorial-kicker text-foreground/45 truncate">
                        {t("dj.by") || "By"} {r.from_name}
                      </p>
                    )}
                  </div>
                </a>
              </MotionReveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default Dj;
